import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import type { ParsedUrlQuery } from '@aurora/shared-utils/helpers/urls/NextRoutes/Route';
import React from 'react';
import { Pagination, useClassNameMapper } from 'react-bootstrap';
import type { EndUserRouteAndParams } from '../../../../routes/endUserRoutes';
import useEndUserRoutes from '../../../../routes/useEndUserRoutes';

interface Props {
  /**
   * Accessibility Label
   */
  ariaLabel: string;

  /**
   * Test ID for element
   */
  testId: string;

  /**
   * The route query params.
   */
  query?: ParsedUrlQuery;

  /**
   * If it is the begining of or end of the list
   */
  isFirstOrLastPage: boolean;
}

/**
 * Renders the link, or in the case of first or last page, the span for the
 * PagerPreviousNextLinkable component.
 *
 * @author Willi Hyde
 */
const PagerPreviousNextLinkableLink: React.FC<React.PropsWithChildren<Props>> = ({
  ariaLabel,
  testId,
  query,
  isFirstOrLastPage,
  children
}) => {
  const cx = useClassNameMapper();

  const { router, Link } = useEndUserRoutes();
  const { params, route } = router.getCurrentRouteAndParams();

  if (isFirstOrLastPage) {
    return (
      <Pagination.Item data-testid={testId} disabled>
        <span className={cx('sr-only')}>{ariaLabel}</span>
        {children}
      </Pagination.Item>
    );
  }

  return (
    <Link<EndUserRouteAndParams<EndUserPages>> route={route} query={query} params={params} passHref>
      <Pagination.Item as="a" data-testid={testId} aria-label={ariaLabel}>
        {children}
      </Pagination.Item>
    </Link>
  );
};

export default PagerPreviousNextLinkableLink;
